/**
 * 编辑器自建元素的 id 规则：`ext-N`（与参考产品一致 —— 原始 id 与小号 ext-* 并存）。
 * 用途：粘贴 / 原地复制时重写 id，避免文档里出现重复 id。
 */
const PREFIX = 'ext-';

/**
 * 下一个 `ext-N`：取**文档里现有 ext-N 的最大值 + 1**。
 * 为什么不是"填空洞"：填洞会在删除后重用旧编号，一旦别处（粘贴的片段、引用的
 * `url(#ext-1)`）还记着那个 id，就会指到新元素上。递增计数器没有这种隐患。
 * `taken` 用于一轮批量重写（同批内也要互不相同）。
 */
export function nextExtId(root: Element, taken?: Set<string>): string {
  const used = taken ?? collectIds(root);
  let max = 0;
  for (const id of used) {
    const hit = /^ext-(\d+)$/.exec(id);
    if (hit) max = Math.max(max, Number(hit[1]));
  }
  const id = `${PREFIX}${max + 1}`;
  used.add(id);
  return id;
}

/** 文档里全部 id */
function collectIds(root: Element): Set<string> {
  const used = new Set<string>();
  root.querySelectorAll('[id]').forEach((el) => {
    const id = el.getAttribute('id');
    if (id) used.add(id);
  });
  return used;
}

/** 深拷贝片段内的所有 id 重写为新 id；返回 旧 id → 新 id 的映射（供反向引用修正） */
export function rewriteIdsInClone(clone: Element, root: Element): Map<string, string> {
  const map = new Map<string, string>();
  const used = collectIds(root);

  if (clone.hasAttribute('id')) {
    const old = clone.getAttribute('id') as string;
    const next = nextExtId(root, used);
    map.set(old, next);
    clone.setAttribute('id', next);
  }
  clone.querySelectorAll('[id]').forEach((node) => {
    const old = node.getAttribute('id') as string;
    const next = nextExtId(root, used);
    map.set(old, next);
    node.setAttribute('id', next);
  });

  // url(#id) / href="#id" 之类引用同步改写，避免断链
  if (map.size > 0) {
    for (const node of [clone, ...Array.from(clone.querySelectorAll('*'))]) {
      for (const attr of Array.from(node.attributes)) {
        let value = attr.value;
        for (const [old, next] of map) {
          if (value.includes(`url(#${old})`)) value = value.split(`url(#${old})`).join(`url(#${next})`);
          if (value === `#${old}`) value = `#${next}`;
        }
        if (value !== attr.value) node.setAttribute(attr.name, value);
      }
    }
  }
  return map;
}
